import React from 'react';
import { SeriesOptionsType, TooltipFormatterContextObject } from 'highcharts';
import { Space } from 'antd';
import { useTheme } from '../../providers/theme';
import { emeraldZincPalette, getColors } from '../utils/getColors';
import { Chart } from './BaseChart';
import { ChartSpec } from './chartSpec';

type ChartResultsProps = {
  containerWidth: string;
  height: string;
  title: string;
  xAxis: string;
  yAxis: string;
  data: {
    name: string;
    data: Array<{ x: number; y: number; [key: string]: any } | number[]>;
    color?: string;
    dashStyle?: string;
    lineWidth?: number;
    type?: 'line' | 'scatter';
    marker?: {
      enabled?: boolean;
      radius?: number;
      symbol?: string;
      fillColor?: string;
      lineColor?: string;
      lineWidth?: number;
    };
  }[];
  yAxisType?: 'linear' | 'logarithmic';
  xAxisMin?: number;
  xAxisMax?: number;
  yAxisMin?: number;
  yAxisMax?: number;
  showLegend?: boolean;
  chartMarginLeft?: number;
  chartMarginRight?: number;
  chartMarginTop?: number;
  chartMarginBottom?: number;
  tooltipFormatter?: (this: TooltipFormatterContextObject) => string;
  backgroundColor?: string;
  borderColor?: string;
  marginTop?: string | number;
};

const ChartResults = ({
  containerWidth,
  height,
  title,
  xAxis,
  yAxis,
  data,
  yAxisType = 'linear',
  xAxisMin,
  xAxisMax,
  yAxisMin,
  yAxisMax,
  showLegend = true,
  chartMarginLeft,
  chartMarginRight,
  chartMarginTop,
  chartMarginBottom,
  tooltipFormatter,
  backgroundColor = 'transparent',
  borderColor,
  marginTop = '1rem'
}: ChartResultsProps) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);

  const computedYMax =
    typeof yAxisMax === 'number'
      ? yAxisMax
      : yAxis === 'Relative Permeability'
      ? 1
      : undefined;
  const computedYMin =
    typeof yAxisMin === 'number'
      ? yAxisMin
      : yAxisType === 'logarithmic'
      ? undefined
      : 0;

  const spec: ChartSpec = {
    type: 'line',
    title,
    colors: emeraldZincPalette,
    height,
    margin: {
      left: chartMarginLeft,
      right: chartMarginRight,
      top: chartMarginTop,
      bottom: chartMarginBottom
    },
    legend: showLegend,
    tooltip: tooltipFormatter,
    xAxis: { title: xAxis, min: xAxisMin, max: xAxisMax },
    yAxis: { title: yAxis, type: yAxisType, min: computedYMin, max: computedYMax },
    plotOptions: {
      line: { marker: { enabled: false } },
      scatter: { marker: { enabled: true, radius: 3 } }
    },
    series: data.map(series => ({
      type: series.type || 'line',
      ...series
    })) as SeriesOptionsType[]
  };

  return (
    <Space
      direction="vertical"
      style={{
        backgroundColor,
        marginTop,
        border: `1px solid ${borderColor ?? colors.border}`,
        borderRadius: 'var(--ds-radius-lg)',
        width: containerWidth,
        transition: 'width 0.5s'
      }}
    >
      <Space style={{ display: 'block' }}>
        <div style={{ padding: '0.75rem' }}>
          <Chart spec={spec} />
        </div>
      </Space>
    </Space>
  );
};

export default ChartResults;
